import { Avatar, Box, CircularProgress, Paper, Stack } from "@mui/material";
import { IconMapPin } from "@tabler/icons-react";
import ImageInput from "../../../../../components/forms/ImageInput";
import { GroupIcon } from "../../../../../components/icons";
import Button from "../../../../../components/ui/Button";
import Input from "../../../../../components/ui/Input";
import RichTextEditor from "../../../../../components/ui/RichTextEditor";
import Select from "../../../../../components/ui/Select";
import Text from "../../../../../components/ui/Text";

const GroupStep = ({
  fetchingGroups,
  existingGroups,
  showCreateGroup,
  groupData,
  onGroupInputChange,
  onImageChange,
  onSelectGroup,
  onCreateNewGroup,
  onUseExistingGroup,
  isGroupSelected,
}) => {
  // Show loading state while checking for groups
  if (fetchingGroups) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Box sx={{ mb: 2 }}>
          <CircularProgress size={24} />
        </Box>
        <Text variant="paragraph3">Checking your groups...</Text>
      </Box>
    );
  }

  // Show create new group form (either no existing groups or user clicked create new)
  if (!existingGroups.length || showCreateGroup) {
    return (
      <Stack spacing={3}>
        <Text component="p" variant="paragraph3" sx={{ color: "#666" }}>
          Save this game to a group so you can keep track of games and members.
        </Text>

        <Box
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: "start",
          }}
        >
          <ImageInput
            value={groupData.image}
            placeholder={<GroupIcon width={88} height={88} />}
            onChange={onImageChange}
          />
        </Box>

        <Input
          label="Group name"
          value={groupData.name}
          onChange={onGroupInputChange("name")}
        />

        <Box sx={{ display: "flex", gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Select
              label="Country"
              placeholder="Select a country"
              value={groupData.country}
              onChange={onGroupInputChange("country")}
              options={[{ value: "Ireland", label: "Ireland" }]}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Select
              label="City"
              placeholder="Select a city"
              value={groupData.city}
              onChange={onGroupInputChange("city")}
              options={[
                { value: "Cork", label: "Cork" },
                { value: "Dublin", label: "Dublin" },
                { value: "Galway", label: "Galway" },
                { value: "Limerick", label: "Limerick" },
              ]}
            />
          </Box>
        </Box>

        <RichTextEditor
          label="Description (optional)"
          value={groupData.description}
          onChange={(html) => {
            onGroupInputChange("description")({ target: { value: html } });
          }}
        />

        {existingGroups.length > 0 && (
          <Button
            variant="tertiary"
            onClick={onUseExistingGroup}
            sx={{ width: "auto", alignSelf: "flex-start" }}
          >
            Use an existing group
          </Button>
        )}
      </Stack>
    );
  }

  // Show existing groups
  return (
    <Stack spacing={3}>
      <Text component="p" variant="paragraph3" sx={{ color: "#666" }}>
        New players who join get added automatically, and we will email them
        about future games.
      </Text>
      {existingGroups.map((group) => (
        <Paper
          key={group.groupId || group.id}
          sx={{
            p: 2,
            border: isGroupSelected(group)
              ? "2px solid #1990FF"
              : "1px solid #E5E5E5",
            borderRadius: "12px",
            cursor: "pointer",
            "&:hover": {
              borderColor: "#1990FF",
            },
            boxShadow: "none",
            backgroundColor: "white",
          }}
          onClick={() => onSelectGroup(group)}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            {/* Group Icon */}
            <Avatar
              src={group?.avatar_image_url}
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "24px",
              }}
            >
              <GroupIcon width={36} height={36} />
            </Avatar>

            {/* Group Details */}
            <Box sx={{ flex: 1 }}>
              <Text
                variant="subtitle2"
                sx={{
                  fontSize: "18px",
                  fontWeight: 600,
                  mb: 1,
                  color: "#000",
                }}
              >
                {group.name}
              </Text>

              <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <IconMapPin
                    size={16}
                    color="#666"
                    style={{ flexShrink: 0 }}
                  />
                  <Text variant="paragraph3" sx={{ color: "#666" }}>
                    {group.city && group.country
                      ? `${group.city}, ${group.country}`
                      : group.location || "Location not set"}
                  </Text>
                </Box>
              </Box>
            </Box>
          </Box>
        </Paper>
      ))}

      {/* Create new group button */}
      <Button
        variant="secondary"
        onClick={onCreateNewGroup}
        sx={{
          mt: 2,
          py: 2,
          borderRadius: "12px",
          border: "1px solid #E5E5E5",
          backgroundColor: "white",
          color: "#000",
          "&:hover": {
            borderColor: "#1990FF",
            backgroundColor: "#F8F9FA",
          },
        }}
      >
        Not this group? Start a new one
      </Button>
    </Stack>
  );
};

export default GroupStep;
